// React lifecycle bindings for the WebMCP runtime.

import { useEffect, useSyncExternalStore } from "react";
import {
  ensureModelContext,
  getAvailability,
  getRegisteredTools,
  registerTools,
  subscribe,
} from "./runtime";
import type { RegisteredTool, WebMcpAvailability, WebMcpScope, WebMcpTool } from "./types";

const EMPTY: RegisteredTool[] = [];

/**
 * Register tools for the lifetime of the calling component.
 * `tools` must be memoised by the caller (useMemo) — the hook re-registers
 * whenever the array identity changes.
 */
export function useWebMcpTools(tools: WebMcpTool[], scope: WebMcpScope): void {
  useEffect(() => registerTools(tools, scope), [tools, scope]);
}

/** Live view of everything this page currently exposes to agents. */
export function useWebMcpState(): {
  availability: WebMcpAvailability;
  tools: RegisteredTool[];
} {
  useEffect(() => {
    void ensureModelContext();
  }, []);

  const tools = useSyncExternalStore(
    subscribe,
    getRegisteredTools,
    () => EMPTY,
  );
  const availability = useSyncExternalStore(
    subscribe,
    getAvailability,
    () => "unknown" as WebMcpAvailability,
  );

  return { availability, tools };
}
